import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePurchasePrice } from "@/contexts/PurchasePriceContext";
import { supabase } from "@/lib/supabase";
import ExcelImportModal from "./components/ExcelImportModal";
import BulkPriceUpdateModal from "./components/BulkPriceUpdateModal";
import { useDealerLog, LOG_ACTIONS } from "@/hooks/useDealerLog";

interface Product {
  id: string;
  name: string;
  product_code: string;
  barcode: string;
  brand: string;
  model: string;
  category_id: string;
  supplier_id: string;
  purchase_price: number;
  sale_price: number;
  stock: number;
  image_url: string;
  shelf_location: string;
  dealer_id: string;
}

interface Category {
  id: string;
  name: string;
}

interface Supplier {
  id: string;
  name: string;
  phone: string;
  email: string;
}

type QuickAddType = "brand" | "model" | "shelf" | "category" | null;

const emptyForm = {
  name: "", productCode: "", barcode: "", brand: "", model: "",
  categoryId: "", supplierId: "", purchasePrice: 0, salePrice: 0,
  imageUrl: "", shelfLocation: "",
};

export default function ProductsPage() {
  const { user } = useAuth();
  const { logAction } = useDealerLog();
  const { showPurchasePrice, hidePurchasePrice, openPasswordModal } = usePurchasePrice();
  const dealerId = user?.dealerId || user?.id || "";

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | "passive">("all");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Quick add
  const [quickAdd, setQuickAdd] = useState<QuickAddType>(null);
  const [quickName, setQuickName] = useState("");
  const [quickSaving, setQuickSaving] = useState(false);

  // Existing values for suggestions
  const existingBrands = [...new Set(products.map((p) => p.brand).filter(Boolean))];
  const existingModels = [...new Set(products.map((p) => p.model).filter(Boolean))];
  const existingShelves = [...new Set(products.map((p) => p.shelf_location).filter(Boolean))];

  // Excel import & bulk price update
  const [showImport, setShowImport] = useState(false);
  const [showBulkPrice, setShowBulkPrice] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [prodRes, catRes, supRes] = await Promise.all([
        supabase.from("products").select("*").eq("dealer_id", dealerId).order("created_at", { ascending: false }),
        supabase.from("categories").select("id, name").eq("dealer_id", dealerId).order("name"),
        supabase.from("suppliers").select("id, name, phone, email").eq("dealer_id", dealerId).order("name"),
      ]);

      if (prodRes.error) throw new Error(prodRes.error.message);
      if (catRes.error) throw new Error(catRes.error.message);
      if (supRes.error) throw new Error(supRes.error.message);

      setProducts(prodRes.data || []);
      setCategories(catRes.data || []);
      setSuppliers(supRes.data || []);
    } catch (err: any) {
      setError(err.message || "Veri yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    if (dealerId) fetchData();
  }, [dealerId, fetchData]);

  const filtered = products
    .filter((p) => {
      if (activeTab === "passive") return (p.stock || 0) <= 0;
      return true;
    })
    .filter((p) => {
      if (!selectedCategory) return true;
      return p.category_id === selectedCategory;
    })
    .filter((p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.product_code || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.barcode || "").includes(search) ||
      (p.brand || "").toLowerCase().includes(search.toLowerCase())
    );

  const passiveCount = products.filter((p) => (p.stock || 0) <= 0).length;
  const lowStockCount = products.filter((p) => (p.stock || 0) > 0 && (p.stock || 0) <= 5).length;

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (p: Product) => {
    setEditing(p);
    setForm({
      name: p.name,
      productCode: p.product_code || "",
      barcode: p.barcode || "",
      brand: p.brand || "",
      model: p.model || "",
      categoryId: p.category_id || "",
      supplierId: p.supplier_id || "",
      purchasePrice: Number(p.purchase_price) || 0,
      salePrice: Number(p.sale_price) || 0,
      imageUrl: p.image_url || "",
      shelfLocation: p.shelf_location || "",
    });
    setShowModal(true);
  };

  const saveProduct = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        dealer_id: dealerId,
        name: form.name.trim(),
        product_code: form.productCode.trim(),
        barcode: form.barcode.trim(),
        brand: form.brand.trim(),
        model: form.model.trim(),
        category_id: form.categoryId || null,
        supplier_id: form.supplierId || null,
        purchase_price: form.purchasePrice,
        sale_price: form.salePrice,
        image_url: form.imageUrl.trim(),
        shelf_location: form.shelfLocation.trim(),
      };

      if (editing) {
        const { error: updateErr } = await supabase
          .from("products")
          .update(payload)
          .eq("id", editing.id);
        if (updateErr) throw new Error(updateErr.message);
        logAction(dealerId, user?.id || null, LOG_ACTIONS.PRODUCT_UPDATE, `"${form.name.trim()}" ürünü güncellendi`);
      } else {
        const { error: insertErr } = await supabase
          .from("products")
          .insert(payload);
        if (insertErr) throw new Error(insertErr.message);
        logAction(dealerId, user?.id || null, LOG_ACTIONS.PRODUCT_ADD, `"${form.name.trim()}" ürünü eklendi`);
      }
      setShowModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.message || "Kayıt sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id: string, name: string) => {
    if (!confirm("Bu ürünü silmek istediğinize emin misiniz?")) return;
    try {
      const { error: delErr } = await supabase.from("products").delete().eq("id", id);
      if (delErr) throw new Error(delErr.message);
      logAction(dealerId, user?.id || null, LOG_ACTIONS.PRODUCT_DELETE, `"${name}" ürünü silindi`);
      fetchData();
    } catch (err: any) {
      setError(err.message || "Silme sırasında hata oluştu.");
    }
  };

  const handleQuickSave = async () => {
    if (!quickName.trim()) return;
    setQuickSaving(true);
    try {
      if (quickAdd === "category") {
        const { error: catErr } = await supabase
          .from("categories")
          .insert({ dealer_id: dealerId, name: quickName.trim() });
        if (catErr) throw new Error(catErr.message);
        logAction(dealerId, user?.id || null, LOG_ACTIONS.CATEGORY_ADD, `Hızlı ekleme: "${quickName.trim()}" kategorisi eklendi`);
        const { data: fresh } = await supabase
          .from("categories")
          .select("id, name")
          .eq("dealer_id", dealerId)
          .order("name");
        setCategories(fresh || []);
        if (fresh && fresh.length > 0) {
          const added = fresh.find((c) => c.name === quickName.trim());
          if (added) setForm((prev) => ({ ...prev, categoryId: added.id }));
        }
      } else if (quickAdd === "brand") {
        setForm((prev) => ({ ...prev, brand: quickName.trim() }));
      } else if (quickAdd === "model") {
        setForm((prev) => ({ ...prev, model: quickName.trim() }));
      } else if (quickAdd === "shelf") {
        setForm((prev) => ({ ...prev, shelfLocation: quickName.trim() }));
      }
      setQuickAdd(null);
      setQuickName("");
    } catch (err: any) {
      setError(err.message || "Ekleme sırasında hata oluştu.");
    } finally {
      setQuickSaving(false);
    }
  };

  const openQuickAdd = (type: QuickAddType) => {
    setQuickName("");
    setQuickAdd(type);
  };

  const getCategoryName = (id: string) => categories.find((c) => c.id === id)?.name || "-";

  const handleAFToggle = () => {
    if (showPurchasePrice) {
      hidePurchasePrice();
    } else {
      openPasswordModal();
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Error banner */}
      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={() => setError("")} className="ml-auto text-accent-700 font-medium cursor-pointer">Kapat</button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="products-title">Ürünler</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">Toplam {products.length} ürün</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ürün ara..." className="w-full pl-9 pr-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <button onClick={handleAFToggle} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${showPurchasePrice ? "bg-accent-100 text-accent-700 hover:bg-accent-200/70" : "bg-background-100 border border-background-200/70 text-foreground-400 hover:bg-background-200/70"}`}>
            <i className={`${showPurchasePrice ? "ri-eye-off-line" : "ri-eye-line"} text-sm`}></i>
            {showPurchasePrice ? "AF Gizle" : "AF Göster"}
          </button>
          <button onClick={fetchData} disabled={loading} className="flex items-center gap-2 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
          </button>
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line"></i> Ürün Ekle
          </button>
          <button onClick={() => setShowImport(true)} className="flex items-center gap-2 px-4 py-2 bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/70 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-file-excel-2-line"></i> Excel Aktar
          </button>
          <button onClick={() => setShowBulkPrice(true)} className="flex items-center gap-2 px-4 py-2 bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/70 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-price-tag-3-line"></i> Toplu Fiyat
          </button>
        </div>
      </div>

      {/* Tab Switcher + Category Filters */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 mb-4 border-b border-background-200/70 pb-3">
        {/* Tabs */}
        <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1 shrink-0">
          <button
            onClick={() => { setActiveTab("all"); setSelectedCategory(null); }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === "all"
                ? "bg-background-50 text-foreground-950"
                : "text-foreground-400 hover:text-foreground-600"
            }`}
          >
            Tüm Ürünler
            {lowStockCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 bg-secondary-100 text-secondary-700 text-[10px] rounded-full font-medium">
                {lowStockCount} düşük
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab("passive"); setSelectedCategory(null); }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === "passive"
                ? "bg-background-50 text-foreground-950"
                : "text-foreground-400 hover:text-foreground-600"
            }`}
          >
            Pasif Ürünler
            {passiveCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 bg-accent-100 text-accent-700 text-[10px] rounded-full font-medium">
                {passiveCount}
              </span>
            )}
          </button>
        </div>

        {/* Category filter pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              !selectedCategory
                ? "bg-secondary-500 text-background-50"
                : "bg-background-100 border border-background-200/70 text-foreground-500 hover:bg-background-200/70"
            }`}
          >
            Hepsi
          </button>
          {categories.map((cat) => {
            const count = products.filter(
              (p) =>
                p.category_id === cat.id &&
                (activeTab === "all" || (p.stock || 0) <= 0)
            ).length;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                  selectedCategory === cat.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/70"
                }`}
                title={`${cat.name} (${count} ürün)`}
              >
                {cat.name}
                <span className={`text-[10px] px-1 py-0.5 rounded-full ${selectedCategory === cat.id ? "bg-background-50/20 text-background-50" : "bg-background-200/60 text-foreground-500"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-3xl block mb-2"></i>
            <p className="text-sm">Yükleniyor...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto hidden md:block">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="bg-background-100 text-left">
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Ürün</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Kod</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Marka</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Raf</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Kategori</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right">Alış</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right">Satış</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right">Stok</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-12 text-center text-foreground-400">
                        <i className="ri-box-3-line text-3xl block mb-2"></i>
                        <p className="text-sm">Henüz ürün eklenmemiş</p>
                        <button onClick={openAdd} className="mt-2 text-primary-600 hover:text-primary-700 text-sm font-medium cursor-pointer">
                          İlk ürünü ekleyin
                        </button>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((p) => (
                      <tr key={p.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {p.image_url ? (
                              <img src={p.image_url} alt={p.name} title={p.name} className="w-9 h-9 rounded-md object-cover shrink-0" />
                            ) : (
                              <div className="w-9 h-9 rounded-md bg-background-100 border border-background-200/70 flex items-center justify-center shrink-0"><i className="ri-image-line text-foreground-300 text-xs"></i></div>
                            )}
                            <div className="min-w-0">
                              <span className="font-medium text-foreground-900 block truncate">{p.name}</span>
                              {p.barcode && <span className="text-[10px] text-foreground-400">{p.barcode}</span>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-foreground-600">{p.product_code || "-"}</td>
                        <td className="px-4 py-3 text-foreground-600">{p.brand || "-"}</td>
                        <td className="px-4 py-3 text-foreground-600">{p.shelf_location || "-"}</td>
                        <td className="px-4 py-3 text-foreground-600">{getCategoryName(p.category_id)}</td>
                        <td className="px-4 py-3 text-foreground-900 text-right font-medium">{showPurchasePrice ? `$${Number(p.purchase_price).toLocaleString("en-US")}` : <span className="text-foreground-300 tracking-widest">***</span>}</td>
                        <td className="px-4 py-3 text-primary-600 text-right font-medium">${Number(p.sale_price).toLocaleString("en-US")}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-medium ${Number(p.stock) > 0 ? "text-foreground-900" : "text-accent-500"}`}>{p.stock || 0}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => openEdit(p)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                            <button onClick={() => deleteProduct(p.id, p.name)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-background-200/70">
              {filtered.length === 0 ? (
                <div className="px-4 py-12 text-center text-foreground-400">
                  <i className="ri-box-3-line text-3xl block mb-2"></i>
                  <p className="text-sm">Henüz ürün eklenmemiş</p>
                  <button onClick={openAdd} className="mt-2 text-primary-600 hover:text-primary-700 text-sm font-medium cursor-pointer">
                    İlk ürünü ekleyin
                  </button>
                </div>
              ) : (
                filtered.map((p) => (
                  <div key={p.id} className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name} title={p.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-background-100 border border-background-200/70 flex items-center justify-center shrink-0"><i className="ri-image-line text-foreground-300"></i></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground-900 truncate">{p.name}</p>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                              {p.brand && <span className="text-xs text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded">{p.brand}</span>}
                              {p.product_code && <span className="text-xs text-foreground-400 font-mono">{p.product_code}</span>}
                              {p.shelf_location && <span className="text-xs text-foreground-400">📍 {p.shelf_location}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => openEdit(p)} className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer"><i className="ri-edit-line"></i></button>
                            <button onClick={() => deleteProduct(p.id, p.name)} className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer"><i className="ri-delete-bin-line"></i></button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-foreground-500">{getCategoryName(p.category_id)}</span>
                            <span className={`text-xs font-semibold ${Number(p.stock) > 0 ? "text-foreground-700" : "text-accent-500"}`}>
                              Stok: {p.stock || 0}
                            </span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-primary-600">${Number(p.sale_price).toLocaleString("en-US")}</p>
                            {showPurchasePrice && <p className="text-[10px] text-foreground-400">Alış: ${Number(p.purchase_price).toLocaleString("en-US")}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950"><a id="product-form">{editing ? "Ürün Düzenle" : "Yeni Ürün"}</a></h4>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-foreground-500 mb-1">Ürün Adı *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Ürün Kodu</label>
                <input type="text" value={form.productCode} onChange={(e) => setForm({ ...form, productCode: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Barkod</label>
                <input type="text" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" />
              </div>

              {/* Brand */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-foreground-500">Marka</label>
                  <button type="button" onClick={() => openQuickAdd("brand")} className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line text-xs"></i> Yeni Marka Ekle
                  </button>
                </div>
                <input type="text" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} list="brand-list" className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" />
                <datalist id="brand-list">
                  {existingBrands.map((b) => <option key={b} value={b} />)}
                </datalist>
              </div>

              {/* Model */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-foreground-500">Model</label>
                  <button type="button" onClick={() => openQuickAdd("model")} className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line text-xs"></i> Yeni Model Ekle
                  </button>
                </div>
                <input type="text" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} list="model-list" className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" />
                <datalist id="model-list">
                  {existingModels.map((m) => <option key={m} value={m} />)}
                </datalist>
              </div>

              {/* Category */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-foreground-500">Kategori</label>
                  <button type="button" onClick={() => openQuickAdd("category")} className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line text-xs"></i> Yeni Kategori Ekle
                  </button>
                </div>
                <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400">
                  <option value="">Seçiniz</option>
                  {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>

              {/* Supplier */}
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Tedarikçi</label>
                <select value={form.supplierId} onChange={(e) => setForm({ ...form, supplierId: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400">
                  <option value="">Seçiniz</option>
                  {suppliers.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </div>

              {/* Shelf Location */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-foreground-500">Raf Yeri</label>
                  <button type="button" onClick={() => openQuickAdd("shelf")} className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line text-xs"></i> Yeni Raf Yeri Ekle
                  </button>
                </div>
                <input type="text" value={form.shelfLocation} onChange={(e) => setForm({ ...form, shelfLocation: e.target.value })} list="shelf-list" placeholder="örn: A-3, B-12" className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" />
                <datalist id="shelf-list">
                  {existingShelves.map((s) => <option key={s} value={s} />)}
                </datalist>
              </div>

              {/* Stock */}
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Stok</label>
                <input type="number" value={editing ? (editing.stock || 0) : 0} readOnly className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-400" />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Alış Fiyatı</label>
                <input type="number" value={form.purchasePrice} onChange={(e) => setForm({ ...form, purchasePrice: Number(e.target.value) })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Satış Fiyatı</label>
                <input type="number" value={form.salePrice} onChange={(e) => setForm({ ...form, salePrice: Number(e.target.value) })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-foreground-500 mb-1">Görsel URL</label>
                <input type="text" value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={saveProduct} disabled={saving} className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap">
                {saving ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>Kaydediliyor...</span> : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Modal */}
      {quickAdd && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={() => setQuickAdd(null)}>
          <div className="bg-background-50 rounded-2xl p-5 w-full max-w-xs mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-foreground-950">
                {quickAdd === "brand" && "Yeni Marka Ekle"}
                {quickAdd === "model" && "Yeni Model Ekle"}
                {quickAdd === "shelf" && "Yeni Raf Yeri Ekle"}
                {quickAdd === "category" && "Yeni Kategori Ekle"}
              </h4>
              <button onClick={() => setQuickAdd(null)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1">
                {quickAdd === "brand" && "Marka Adı"}
                {quickAdd === "model" && "Model Adı"}
                {quickAdd === "shelf" && "Raf Yeri Adı"}
                {quickAdd === "category" && "Kategori Adı"}
              </label>
              <input
                type="text"
                value={quickName}
                onChange={(e) => setQuickName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleQuickSave(); }}
                autoFocus
                className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                placeholder={
                  quickAdd === "brand" ? "Marka adını yazın" :
                  quickAdd === "model" ? "Model adını yazın" :
                  quickAdd === "shelf" ? "örn: A-3, B-12" : "Kategori adını yazın"
                }
              />
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setQuickAdd(null)} className="flex-1 py-2 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={handleQuickSave} disabled={quickSaving || !quickName.trim()} className="flex-1 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap">
                {quickSaving ? "Ekleniyor..." : "Ekle"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Import Modal */}
      {showImport && (
        <ExcelImportModal
          dealerId={dealerId}
          onClose={() => setShowImport(false)}
          onSuccess={() => fetchData()}
        />
      )}

      {/* Bulk Price Update Modal */}
      {showBulkPrice && (
        <BulkPriceUpdateModal
          products={products.map((p) => ({
            id: p.id,
            name: p.name,
            product_code: p.product_code,
            brand: p.brand,
            purchase_price: p.purchase_price,
            sale_price: p.sale_price,
          }))}
          dealerId={dealerId}
          onClose={() => setShowBulkPrice(false)}
          onSuccess={() => fetchData()}
        />
      )}
    </div>
  );
}